import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Bell, Camera, Compass, Home, LogIn, Menu, Plus } from 'lucide-react';

import FloatingCreatePost from '../feed/FloatingCreatePost';
import { useAuthStore } from '../../stores/auth.store';

/**
 * Pages où la barre s'affiche : le fil et ce qui gravite autour. Les écrans de
 * gestion du club (/club/…) ont leurs propres barres d'action en bas, qu'elle
 * masquerait.
 */
const PAGES = ['/feed', '/suivre', '/notifications', '/profile', '/jeux', '/programmes', '/marketplace', '/photographe'];

export function barreMobileVisible(chemin: string): boolean {
  return PAGES.some((page) => chemin === page || chemin.startsWith(`${page}/`));
}

type OngletProps = {
  to: string;
  libelle: string;
  Icone: typeof Home;
  actif: boolean;
  pastille?: number;
};

function Onglet({ to, libelle, Icone, actif, pastille }: OngletProps) {
  return (
    <Link
      to={to}
      aria-current={actif ? 'page' : undefined}
      className={`flex flex-1 flex-col items-center justify-center gap-0.5 py-1.5 text-[11px] font-medium transition-colors ${
        actif ? 'text-green-700' : 'text-gray-500 active:text-gray-800'
      }`}
    >
      <span className="relative">
        <Icone className="h-6 w-6" strokeWidth={actif ? 2.4 : 1.8} />
        {!!pastille && pastille > 0 && (
          <span className="absolute -right-2 -top-1.5 min-w-[18px] rounded-full bg-red-500 px-1 text-center text-[10px] font-bold leading-[18px] text-white">
            {pastille > 99 ? '99+' : pastille}
          </span>
        )}
      </span>
      {libelle}
    </Link>
  );
}

type Props = {
  notificationsNonLues: number;
  /** Ouvre le tiroir du menu complet (profil, rubriques, panier, déconnexion). */
  onOuvrirMenu: () => void;
};

function BoutonMenu({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Ouvrir le menu"
      className="flex flex-1 flex-col items-center justify-center gap-0.5 py-1.5 text-[11px] font-medium text-gray-500 active:text-gray-800"
    >
      <Menu className="h-6 w-6" strokeWidth={1.8} />
      Menu
    </button>
  );
}

/**
 * La navigation du bas, comme dans une application : toujours à portée de
 * pouce sur téléphone et tablette, là où le menu du haut demande d'étirer la
 * main. Le « + » central ouvre directement la publication.
 *
 * Masquée à partir de 1024 px : la barre du haut y affiche déjà tout.
 */
export default function BarreNavigationMobile({ notificationsNonLues, onOuvrirMenu }: Props) {
  const { pathname } = useLocation();
  const { isAuthenticated, user } = useAuthStore();
  const [publication, setPublication] = useState(false);

  if (!barreMobileVisible(pathname)) return null;

  const photographe = user?.user_type === 'photographer';
  const actif = (chemin: string) => pathname === chemin || pathname.startsWith(`${chemin}/`);

  return (
    <>
      {/* Réserve la hauteur de la barre : le bas de la page reste lisible. */}
      <div aria-hidden className="h-[calc(4rem+env(safe-area-inset-bottom))] lg:hidden" />

      <nav
        aria-label="Navigation principale"
        className="fixed inset-x-0 bottom-0 z-40 border-t border-gray-200 bg-white/95 pb-[env(safe-area-inset-bottom)] backdrop-blur lg:hidden"
      >
        <div className="mx-auto flex h-16 max-w-xl items-stretch px-2">
          <Onglet to="/feed" libelle="Accueil" Icone={Home} actif={actif('/feed')} />
          {photographe ? (
            <Onglet to="/photographe" libelle="Mon espace" Icone={Camera} actif={actif('/photographe')} />
          ) : (
            <Onglet to="/suivre" libelle="Découvrir" Icone={Compass} actif={actif('/suivre')} />
          )}

          {isAuthenticated ? (
            <>
              <div className="flex flex-1 items-start justify-center">
                <button
                  type="button"
                  onClick={() => setPublication(true)}
                  aria-label="Publier"
                  className="-mt-5 flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-green-600 to-green-700 text-white shadow-lg shadow-green-600/30 ring-4 ring-white transition active:scale-95"
                >
                  <Plus className="h-7 w-7" strokeWidth={2.5} />
                </button>
              </div>
              <Onglet
                to="/notifications"
                libelle="Alertes"
                Icone={Bell}
                actif={actif('/notifications')}
                pastille={notificationsNonLues}
              />
              {/* Le profil, les autres rubriques et la déconnexion sont dans le menu :
                  la barre du haut n'existe plus sur ces pages. */}
              <BoutonMenu onClick={onOuvrirMenu} />
            </>
          ) : (
            <>
              <Link
                to="/login"
                className="m-2 flex flex-[2] items-center justify-center gap-2 rounded-xl bg-green-600 text-sm font-semibold text-white active:bg-green-700"
              >
                <LogIn className="h-5 w-5" />
                Se connecter
              </Link>
              <BoutonMenu onClick={onOuvrirMenu} />
            </>
          )}
        </div>
      </nav>

      {isAuthenticated && <FloatingCreatePost ouvert={publication} onFermer={() => setPublication(false)} />}
    </>
  );
}
